/**
 * THE DOOR to every page of the new design (PV1b: generalised from the home page's door, design-system §7b).
 *
 * It is on the first download, so it holds nothing but the one dynamic import of the SHELL (the `pavilion` chunk —
 * the frame, header, footer, stylesheet, drawn water and the page registry); the shell's registry then loads the page's
 * own chunk (`pavilion-<key>`), through the kit's chunk (`pavilion-kit`) for every page but the home page. The registry and the shell are imported for their TYPES only: a value import
 * would carry every page's loader onto every visitor's first download (scripts/check-chat-first-paint.mjs R4).
 *
 * WHY useDeferredModule AND NOT React.lazy: App.tsx records React.lazy stalling on the FIRST mount of `/` in
 * production (2026-05-09), and a door mounts on exactly that first render. The hook is a plain import() resolved into
 * state — no Suspense, nothing thrown during render — and it retries a failed download three times before handing a
 * chunk that is gone for good to the stale-tab self-heal (src/utils/useDeferredModule.ts).
 *
 * THE DOOR WAITS FOR BOTH CHUNKS, THEN RENDERS ONCE. Its loader is one promise — the shell, then the page — so the
 * arrival is still one state update. While they arrive the door paints the pavilion's sky in the theme the page will
 * open in, so the arrival is a calm fade rather than a flash of the wrong ground. If either cannot be had at all, the
 * visitor gets today's page — the same app — never a blank one.
 */
import React from 'react';
import { useDeferredModule } from '../../utils/useDeferredModule';
import { semanticDark, semanticLight } from '../../styles/semanticTokens.generated';
import { designShows } from '../previewPath';
import { resolvePreviewTheme } from '../previewTheme';
import { PageLight } from '../PageLight';
import { preloadPavilionFont } from './pavilionFont';
import { claimAppChrome } from './appChrome';
import type { PavilionPageKey } from './shell/pageRegistry';
import type { PavilionShellProps } from './shell/PavilionShell';
import type { PavilionTopUpDialogProps } from './topup/PavilionTopUpDialog';

export type { PavilionPageKey };

// The ONE import of the shell (R4 of scripts/check-chat-first-paint.mjs counts it): every door and the boot-time
// preload call it, and webpack shares one request between them.
const loadShell = () => import(/* webpackChunkName: "pavilion" */ './shell/PavilionShell');

export interface LoadedPavilion {
  Shell: React.ComponentType<PavilionShellProps>;
  Page: React.ComponentType;
}

/** The shell, then the page it frames, as one promise. */
export function loadPavilion(key: PavilionPageKey): Promise<LoadedPavilion> {
  return loadShell().then((shell) => shell.loadPage(key).then((Page) => ({ Shell: shell.default, Page })));
}

/**
 * Start fetching a page before the first render (src/index.tsx calls it for `/` when the page will show the new
 * design), so both requests are on the wire while main.js is still being parsed instead of after the router mounts
 * the door. Never throws; a failure is the door's to handle (it retries, then falls back to today's page).
 */
export function preloadPavilion(key: PavilionPageKey): void {
  preloadPavilionFont();
  loadPavilion(key).catch(() => undefined);
}

export interface PavilionDoorProps {
  pageKey: PavilionPageKey;
  /** What renders if the new design's chunks cannot be loaded: today's page. */
  fallback: React.ReactNode;
  /**
   * The page stands under the app header (its registry row says chrome 'app'), which App.tsx draws: the door claims no
   * header for it. Declared where App.tsx declares the page, because the door cannot read the registry's values.
   */
  appChrome?: boolean;
}

export const PavilionDoor: React.FC<PavilionDoorProps> = ({ pageKey, fallback, appChrome = false }) => {
  // The face travels while the chunks do, so the headline paints in it rather than swapping to it.
  React.useEffect(() => preloadPavilionFont(), []);
  const { module, error } = useDeferredModule(() => loadPavilion(pageKey), `pavilion:${pageKey}`, { start: 'mount' });
  // The page brings its own header: today's global one stands down while the door is loading or showing it, and
  // comes back if the door falls back to today's page (./appChrome.ts; App.tsx reads the claim). An app page brings
  // none: the app header above it is App.tsx's, so it claims nothing.
  React.useLayoutEffect(() => (error || appChrome ? undefined : claimAppChrome('header')), [error, appChrome]);

  if (module) {
    const { Shell, Page } = module;
    return <Shell pageKey={pageKey} Page={Page} />;
  }
  if (error) return <>{fallback}</>;

  const dark = resolvePreviewTheme() === 'dark';
  return (
    <>
      {/* The chat takes the light the page opens in, even while the page's chunk arrives (PR-4a.2); never in the
          fallback above, which is today's light-only page. */}
      <PageLight theme={dark ? 'dark' : 'light'} />
      <div
        data-pavilion-door=""
        aria-busy="true"
        style={{ minHeight: '100vh', background: dark ? semanticDark.canvas : semanticLight.canvas }}
      />
    </>
  );
};

/** The app header in the new design (PV5-8:D), through the shell (the one import of it, above) and the kit. */
export function loadPavilionAppHeader(): Promise<React.ComponentType> {
  return loadShell().then((shell) => shell.loadAppHeader());
}

/** The band's height while it arrives: the new header's own (72px, 64px at 720px and under), so nothing moves when it lands. */
function appHeaderHeight(): number {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 720px)').matches ? 64 : 72;
}

export interface PavilionAppHeaderDoorProps {
  /** What renders if the new design's chunks cannot be loaded: today's header. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the app header in the new design (PV5-8:D): App.tsx draws it where today's header stands, while the
 * `app` surface shows. Loaded the pages' way (a plain import() resolved into state, never React.lazy), and today's
 * header is its fallback if the chunks cannot be had. While they arrive it holds the band's place at the band's height.
 */
export const PavilionAppHeaderDoor: React.FC<PavilionAppHeaderDoorProps> = ({ fallback }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: AppHeader, error } = useDeferredModule(loadPavilionAppHeader, 'pavilion:app-header', { start: 'mount' });
  if (AppHeader) return <AppHeader />;
  if (error) return <>{fallback}</>;
  return <div data-pavilion-app-door="" aria-busy="true" style={{ height: appHeaderHeight(), background: semanticLight.canvas }} />;
};

/** The top-up dialog in the new design (PV10 part 2), through the shell (the one import of it, above) and the kit. */
export function loadPavilionTopUpDialog(): Promise<React.ComponentType<PavilionTopUpDialogProps>> {
  return loadShell().then((shell) => shell.loadTopUpDialog());
}

/** Fetch the top-up dialog's chunks ahead of a press. Never throws: a failure is the door's to handle. */
export function preloadPavilionTopUpDialog(): void {
  loadPavilionTopUpDialog().catch(() => undefined);
}

export interface PavilionTopUpDoorProps extends PavilionTopUpDialogProps {
  /** What renders if the new design's chunks cannot be loaded: today's dialog, so a person can always pay. */
  fallback: React.ReactNode;
}

/**
 * THE DOOR to the top-up dialog in the new design (PV10 part 2): TopUpModalProvider draws it, inside Stripe's provider,
 * where the `top-up` surface shows. Loaded the pages' way (a plain import() resolved into state, never React.lazy), with
 * today's dialog as its fallback. It draws nothing while the chunks arrive: the provider fetches them at the first idle
 * moment for a signed-in person (preloadPavilionTopUpDialog), so by the time anyone presses "top up" they are here.
 */
export const PavilionTopUpDoor: React.FC<PavilionTopUpDoorProps> = ({ fallback, ...props }) => {
  React.useEffect(() => preloadPavilionFont(), []);
  const { module: TopUpDialog, error } = useDeferredModule(loadPavilionTopUpDialog, 'pavilion:top-up', { start: 'mount' });
  if (TopUpDialog) return <TopUpDialog {...props} />;
  if (error) return <>{fallback}</>;
  return null;
};

/**
 * A page of the app, shown in the new design where its surface word says so. Wrap the page where App.tsx declares it
 * (`const SafeAbout = withSafeAnimation(withPavilion(About, 'about'))`), never on a route line: the route stays
 * `element={<SafeAbout />}`. The coverage gate follows the route to that one declaration. A page that stands under the
 * app header says so here (`withPavilion(AgentActivity, 'desktop', { chrome: 'app' })`), as its registry row does.
 */
export function withPavilion<P extends object>(
  Today: React.ComponentType<P>,
  key: PavilionPageKey,
  options: { chrome?: 'app' } = {},
): React.FC<P> {
  const appChrome = options.chrome === 'app';
  const Wrapped: React.FC<P> = (props) =>
    designShows(key)
      ? <PavilionDoor pageKey={key} fallback={<Today {...props} />} appChrome={appChrome} />
      : <Today {...props} />;
  Wrapped.displayName = `withPavilion(${key})`;
  return Wrapped;
}
